import { motion } from 'framer-motion';
import { ChevronRight, Download, FlaskConical, Layers } from 'lucide-react';
import DriverStatusBadge from './DriverStatusBadge';
import { sourceIcon } from './sourceState';
import type { DriverListItem, DriverSourceRef } from './types';
import type { ConductRiskSourceLink } from '../../../api/apiTypesConductRisk';

interface Props {
  /** Driver 1..9 — partecipano alla generazione del report. */
  analisi:         DriverListItem[];
  /** Driver 10+ — analisi manuali Compliance, escluse dal report. */
  investigation:   DriverListItem[];
  /** Fonti caricate del report — usate per rendere i badge cliccabili (download). */
  sourceLinks:     ConductRiskSourceLink[];
  onDriverClick:   (driver: DriverListItem) => void;
  /** Chiamato al click su un badge fonte. `link` e' undefined se la fonte non
   *  e' stata ancora caricata (badge in stato "missing"). */
  onSourceClick?:  (link: ConductRiskSourceLink | undefined, label: string) => void;
  /** Click sulla card "Aggregazione Evidenze Investigation" (tabella di riepilogo). */
  onSummaryClick?: () => void;
}

// Palette per categoria:
//  - analisi        = brand navy (Driver 1..9, cliccabili);
//  - investigation  = amber "attention" (Driver 10+, cliccabili);
//  - annex          = grigio muted, sola consultazione (non cliccabili).
const palette = {
  analisi: {
    titleClr:      'text-[#1B365D]',
    cardBg:        'bg-white',
    cardBorder:    'border-gray-100',
    cardHover:     'hover:border-[#1B365D]/20 hover:shadow-sm',
    iconBg:        'bg-gray-50 group-hover:bg-[#1B365D]/5',
    iconClr:       'text-[#1B365D]/60',
    nameClr:       'text-[#1B365D]',
    chipBg:        'bg-gray-50 border-gray-100 text-gray-500',
    chipInteractive: 'hover:bg-[#1B365D]/10 hover:border-[#1B365D]/30 hover:text-[#1B365D] cursor-pointer',
    chevronHover:  'group-hover:text-[#1B365D] group-hover:bg-[#1B365D]/5',
  },
  investigation: {
    titleClr:      'text-amber-800',
    cardBg:        'bg-amber-50/40',
    cardBorder:    'border-amber-100',
    cardHover:     'hover:border-amber-300 hover:shadow-sm',
    iconBg:        'bg-amber-100/60 group-hover:bg-amber-200/70',
    iconClr:       'text-amber-700',
    nameClr:       'text-amber-900',
    chipBg:        'bg-amber-50 border-amber-100 text-amber-800',
    chipInteractive: 'hover:bg-amber-100 hover:border-amber-300 cursor-pointer',
    chevronHover:  'group-hover:text-amber-700 group-hover:bg-amber-100/60',
  },
  annex: {
    titleClr:      'text-gray-600',
    cardBg:        'bg-gray-50/60',
    cardBorder:    'border-gray-200/70',
    cardHover:     '',                                 // no hover: card statica
    iconBg:        'bg-gray-100',
    iconClr:       'text-gray-500',
    nameClr:       'text-gray-700',
    chipBg:        'bg-white border-gray-200 text-gray-500',
    chipInteractive: 'hover:bg-gray-100 hover:border-gray-300 cursor-pointer',
    chevronHover:  '',
  },
};

/** Per una fonte del driver, ritorna la lista dei link caricati (0..N).
 *  N > 1 solo per document_type multiple (email, pvr_quota_optional). */
const linksFor = (
  src: DriverSourceRef,
  allLinks: ConductRiskSourceLink[],
): ConductRiskSourceLink[] => {
  if (!src.docType) return [];
  const target = String(src.docType).trim().toLowerCase();
  return allLinks.filter(l => String(l.document_type ?? '').trim().toLowerCase() === target);
};

interface SourceBadgeProps {
  label:     string;
  link?:     ConductRiskSourceLink;
  chipBg:    string;
  chipInteractive: string;
  onClick?:  (link: ConductRiskSourceLink | undefined, label: string) => void;
}

const SourceBadge = ({ label, link, chipBg, chipInteractive, onClick }: SourceBadgeProps) => {
  const isDownloadable = !!link;
  // Badge SEMPRE cliccabile: se la fonte non e' caricata il container mostra
  // un feedback informativo, altrimenti scarica il file. Il click NON deve
  // mai bubblare al driver card (stopPropagation + preventDefault).
  return (
    <button
      type="button"
      onClick={e => {
        e.stopPropagation();
        e.preventDefault();
        onClick?.(link, label);
      }}
      onMouseDown={e => e.stopPropagation()}
      onPointerDown={e => e.stopPropagation()}
      title={link?.source?.file_name ?? `${label} — fonte non ancora caricata`}
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] border ${chipBg} ${isDownloadable ? chipInteractive : 'opacity-60 hover:opacity-80'} cursor-pointer transition-colors`}
    >
      {sourceIcon(label)}
      <span className="max-w-[160px] truncate">{label}</span>
      {isDownloadable && <Download className="w-2.5 h-2.5 shrink-0" />}
    </button>
  );
};

interface CardProps {
  driver:        DriverListItem;
  index:         number;
  sourceLinks:   ConductRiskSourceLink[];
  onClick:       (d: DriverListItem) => void;
  onSourceClick?: (link: ConductRiskSourceLink | undefined, label: string) => void;
}

const Card = ({ driver, index, sourceLinks, onClick, onSourceClick }: CardProps) => {
  const Icon = driver.icon;
  const p = palette[driver.category];
  // Gli Annex sono di sola consultazione: no click, no hover, no chevron,
  // niente cursor-pointer. Il badge fonte rimane cliccabile (scarica l'Annex).
  const isInteractive = driver.category !== 'annex';
  return (
    <motion.div
      key={driver.kind}
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
      onClick={isInteractive ? () => onClick(driver) : undefined}
      className={`flex items-center gap-4 p-4 ${p.cardBg} rounded-xl border ${p.cardBorder} ${p.cardHover} transition-all group ${isInteractive ? 'cursor-pointer' : 'cursor-default'}`}
    >
      <div className="flex items-center gap-3 shrink-0">
        <div className={`w-9 h-9 rounded-lg ${p.iconBg} flex items-center justify-center transition-colors`}>
          <Icon className={`w-4 h-4 ${p.iconClr}`} />
        </div>
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-0.5">
          <span className={`text-sm font-semibold ${p.nameClr}`}>{driver.name}</span>
        </div>
        <p className="text-xs text-gray-500 truncate">{driver.description}</p>
        <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
          {driver.sources.flatMap((src, si) => {
            const links = linksFor(src, sourceLinks);
            // Nessun file caricato: badge label statico (non cliccabile).
            if (links.length === 0) {
              return [
                <SourceBadge
                  key={`${driver.kind}-${si}-empty`}
                  label={src.label}
                  chipBg={p.chipBg}
                  chipInteractive={p.chipInteractive}
                  onClick={onSourceClick}
                />,
              ];
            }
            // 1..N file caricati: un badge per file (per document_type multiple).
            return links.map((link, li) => (
              <SourceBadge
                key={`${driver.kind}-${si}-${link.id}`}
                label={links.length > 1 ? `${src.label} · ${li + 1}` : src.label}
                link={link}
                chipBg={p.chipBg}
                chipInteractive={p.chipInteractive}
                onClick={onSourceClick}
              />
            ));
          })}
        </div>
      </div>

      <div className="flex items-center gap-3 shrink-0">
        <DriverStatusBadge status={driver.status} />
        {/* Chevron (o spacer invisibile della stessa larghezza sugli annex),
         *  cosi' lo status badge resta allineato con le card cliccabili. */}
        {isInteractive ? (
          <button className={`w-7 h-7 rounded-lg flex items-center justify-center text-gray-300 ${p.chevronHover} transition-colors`}>
            <ChevronRight className="w-4 h-4" />
          </button>
        ) : (
          <span className="w-7 h-7" aria-hidden />
        )}
      </div>
    </motion.div>
  );
};

const DriverList = ({ analisi, investigation, sourceLinks, onDriverClick, onSourceClick, onSummaryClick }: Props) => {
  const analisiGenerated = analisi.filter(d => d.status !== 'empty').length;

  return (
    <>
      {/* ─── Driver di analisi (1..9) ─────────────────────────── */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-base font-semibold text-[#1B365D]">Driver di analisi</h2>
          <p className="text-xs text-gray-400 mt-0.5">
            {analisiGenerated}/{analisi.length} driver generati
          </p>
        </div>
      </div>

      <div className="space-y-2">
        {analisi.map((driver, i) => (
          <Card
            key={driver.kind}
            driver={driver}
            index={i}
            sourceLinks={sourceLinks}
            onClick={onDriverClick}
            onSourceClick={onSourceClick}
          />
        ))}
      </div>

      {/* ─── Driver di Investigation (10+) ────────────────────── */}
      {investigation.length > 0 && (
        <>
          <div className="flex items-center justify-between pt-2">
            <div className="flex items-center gap-2">
              <FlaskConical className="w-4 h-4 text-amber-700" />
              <div>
                <h2 className="text-base font-semibold text-amber-800">Driver di Investigation</h2>
                <p className="text-xs text-gray-400 mt-0.5">
                  Analisi manuali Compliance — non incluse nella generazione del report
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-2">
            {investigation.map((driver, i) => (
              <Card
                key={driver.kind}
                driver={driver}
                index={i}
                sourceLinks={sourceLinks}
                onClick={onDriverClick}
                onSourceClick={onSourceClick}
              />
            ))}

            {/* Aggregazione Evidenze Investigation — non e' un driver: tabella
             *  di riepilogo servita da un endpoint dedicato. Mostrata come
             *  ultima card della sezione Investigation. */}
            {onSummaryClick && (
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: investigation.length * 0.05 }}
                onClick={onSummaryClick}
                className="flex items-center gap-4 p-4 bg-amber-50/40 rounded-xl border border-amber-100 hover:border-amber-300 hover:shadow-sm transition-all group cursor-pointer"
              >
                <div className="flex items-center gap-3 shrink-0">
                  <div className="w-9 h-9 rounded-lg bg-amber-100/60 group-hover:bg-amber-200/70 flex items-center justify-center transition-colors">
                    <Layers className="w-4 h-4 text-amber-700" />
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="text-sm font-semibold text-amber-900">Aggregazione Evidenze Investigation</span>
                  </div>
                  <p className="text-xs text-gray-500 truncate">
                    Tabella di riepilogo che aggrega per filiale le evidenze rilevate dai driver di Investigation.
                  </p>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <button className="w-7 h-7 rounded-lg flex items-center justify-center text-gray-300 group-hover:text-amber-700 group-hover:bg-amber-100/60 transition-colors">
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </motion.div>
            )}
          </div>
        </>
      )}
    </>
  );
};

export default DriverList;
